import { useContext, useSyncExternalStore, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, LockOpen } from 'lucide-react';
import type { ObjectData, SceneStore } from '@os/doc';
import { Icon } from '../../ui/icons';
import { sectionAccent } from '../../ui/section-colors';
import { ExportModeContext } from '../editor/embeds';

type Section = Extract<ObjectData, { type: 'section' }>;

/**
 * A section's fill and, above its top-left corner, a row at a constant screen size (the controller writes
 * `--inv-zoom` on the world layer) truncated to the section's width: the title pill and the padlock (P5e).
 * The pill is neutral at rest and takes the section's colour when the controller marks it `data-section-selected`.
 * The title carries `data-section-grab`: a press there moves the section (the controller passes it to the engine,
 * the row being outside the box); with the 16 px contour it is the only way to drag one (no grip since P5g), so while
 * it can move, the pill shows the move cursor and says so in its tooltip. Double-click or F2 renames (`SectionTitleEditor`). A frame (`clip`) is square and
 * clips its children in `ObjectView`, never this row. The padlock shows a locked section (its content is locked too)
 * and toggles the lock; unlocked, it only appears, and only takes clicks, while the section is hovered. It is canvas UI:
 * its own click reaches it (pointer capture would retarget it). In an export, only the title, inside the corner.
 * Collapsed, its bar says how many objects it hides (`hidden`, its direct children), in an export too.
 */
export function SectionObject({ o, store, hidden = 0 }: { o: Section; store: SceneStore; hidden?: number }) {
  const { t } = useTranslation();
  const readOnly = useSyncExternalStore(
    (cb) => store.subscribeReadOnly(cb),
    () => store.readOnly,
  );
  const exporting = useContext(ExportModeContext);
  const name = o.name || t('editor.sectionPlaceholder');
  const movable = !readOnly && !o.locked;
  const tone = sectionAccent(o.fill);
  const accent = { '--section-accent': tone } as CSSProperties;
  const fill = (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 border border-black/10 [[data-section-hover]>&]:border-[var(--section-accent,#52525b)] [[data-section-selected]>&]:border-[var(--section-accent,#52525b)] [[data-section-selected]>&]:shadow-[0_0_0_1px_var(--section-accent,#52525b)] ${o.clip ? '' : 'rounded-xl'}`}
      style={{ ...accent, background: o.fill }}
    />
  );
  const count = hidden > 0 && (
    <div data-section-count className="pointer-events-none absolute inset-0 flex items-center justify-end truncate px-3 font-sans text-sm font-medium" style={{ color: tone }}>
      {t('editor.sectionHidden', { count: hidden })}
    </div>
  );
  if (exporting)
    return (
      <>
        {fill}
        {count}
        <div
          data-section-title={o.id}
          className="absolute left-0 top-0 origin-top-left whitespace-nowrap px-3 pt-2 font-sans text-sm font-semibold text-[#3f3f46]"
          style={{ transform: 'scale(var(--inv-zoom, 1))', maxWidth: `calc(${o.w}px / var(--inv-zoom, 1))` }}
        >
          <span className="block truncate">{name}</span>
        </div>
      </>
    );
  return (
    <>
      {fill}
      {count}
      <div
        data-section-row
        className="absolute bottom-full left-0 flex origin-bottom-left items-center gap-1 pb-1.5"
        style={{ ...accent, transform: 'scale(var(--inv-zoom, 1))', maxWidth: `calc(${o.w}px / var(--inv-zoom, 1))` }}
      >
        <div
          data-section-title={o.id}
          data-section-grab={o.id}
          title={movable ? t('editor.dragToMove') : undefined}
          className={`min-w-0 ${movable ? 'cursor-move' : ''} whitespace-nowrap rounded-lg border border-transparent bg-[#ebebe7] px-2.5 py-1 pointer-coarse:py-3 font-sans text-sm font-semibold text-[#3f3f46] [[data-section-selected]>div>&]:border-[var(--section-accent,#52525b)] [[data-section-selected]>div>&]:bg-[var(--section-accent,#52525b)] [[data-section-selected]>div>&]:text-white`}
        >
          <span className={`block truncate ${o.name ? '' : 'opacity-70'}`}>{name}</span>
        </div>
        <button
          type="button"
          data-section-lock={o.id}
          data-canvas-ignore
          aria-pressed={o.locked}
          aria-label={t(o.locked ? 'editor.unlockSection' : 'editor.lockSection')}
          title={t(o.locked ? 'editor.unlockSection' : 'editor.lockSection')}
          disabled={readOnly}
          onClick={() => store.commands.setProps([o.id], { locked: !o.locked })}
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-fg shadow-sm disabled:cursor-default ${o.locked ? '' : 'pointer-events-none opacity-0 focus-visible:opacity-100 [[data-id]:hover>div>&]:pointer-events-auto [[data-id]:hover>div>&]:opacity-100'}`}
        >
          <Icon icon={o.locked ? Lock : LockOpen} size={14} />
        </button>
      </div>
    </>
  );
}
